"use client"

import { Button, Card, cn } from "@rarible_int/anaconda_ui_components"
import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import type { GachaSeriesView } from "./gacha-series"
import {
  getLowestMachinePriceLabel,
  getMachinePackPreview,
} from "./gacha-series"

type GachaSeriesCardProps = {
  series: GachaSeriesView
  onOpenPack: () => void
}

export function GachaSeriesCard({ series, onOpenPack }: GachaSeriesCardProps) {
  const router = useRouter()
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const carouselItems = [
    {
      key: `${series.slug}-category`,
      image: series.image,
      videoSrc: undefined,
      label: series.eyebrow,
      ariaLabel: `${series.name} category preview`,
      machine: null,
      source: "category" as const,
    },
    ...series.machines.map((machine) => ({
      key: machine.code,
      ...getMachinePackPreview(machine),
      label: machine.name,
      ariaLabel: `${machine.name} preview`,
      machine,
    })),
  ]
  const activeItem = carouselItems[activeImageIndex] ?? carouselItems[0]
  const activeImage = activeItem?.image ?? series.image
  const activePackName = activeItem?.label ?? series.eyebrow
  const lowestPrice = getLowestMachinePriceLabel(series.machines)
  const detailHref = `/gacha/${encodeURIComponent(series.slug)}`

  useEffect(() => {
    router.prefetch(detailHref)
  }, [detailHref, router])

  const startPreview = () => {
    setIsPreviewing(true)
  }

  useEffect(() => {
    if (!isPreviewing) {
      setActiveImageIndex(0)
      return
    }

    const id = window.setInterval(() => {
      setActiveImageIndex((index) => (index + 1) % carouselItems.length)
    }, 1200)

    return () => window.clearInterval(id)
  }, [carouselItems.length, isPreviewing])

  return (
    // biome-ignore lint/a11y/useSemanticElements: Matches the UI branch card pattern while preserving nested preview controls.
    <Card
      role="button"
      tabIndex={0}
      className="group flex cursor-pointer flex-col overflow-hidden p-0 transition-colors hover:bg-foreground/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      onMouseEnter={startPreview}
      onMouseLeave={() => setIsPreviewing(false)}
      onPointerEnter={startPreview}
      onPointerLeave={() => setIsPreviewing(false)}
      onFocus={startPreview}
      onBlur={(event) => {
        const nextTarget = event.relatedTarget as Node | null
        if (!event.currentTarget.contains(nextTarget)) {
          setIsPreviewing(false)
        }
      }}
      onClick={() => router.push(detailHref)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          router.push(detailHref)
        }
      }}
    >
      <div className="relative aspect-square overflow-hidden bg-[#101010]">
        {activeImageIndex > 0 ? (
          <div className="absolute left-3 top-3 z-20 max-w-[calc(100%-1.5rem)] truncate rounded-full border border-foreground/10 bg-background/70 px-2 py-1 font-mono text-3xs uppercase leading-none text-foreground backdrop-blur-md">
            {activePackName}
          </div>
        ) : null}
        {carouselItems.map((item, index) =>
          shouldUsePackShowcase({ index, source: item.source }) ? (
            <div
              key={item.key}
              className={cn(
                "absolute inset-0 transition-opacity duration-500",
                index === activeImageIndex ? "opacity-100" : "opacity-0",
              )}
            >
              <PackShowcase image={item.image} videoSrc={item.videoSrc} />
            </div>
          ) : (
            <img
              key={item.key}
              src={item.image}
              alt=""
              className={cn(
                "absolute inset-0 h-full w-full object-contain p-4 transition-opacity duration-500",
                index === activeImageIndex ? "opacity-100" : "opacity-0",
              )}
            />
          ),
        )}
        <div
          className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1 rounded-full bg-background/60 px-2 py-1 backdrop-blur-md"
          aria-label={`${series.name} pack previews`}
        >
          {carouselItems.map((item, index) => (
            <button
              key={`${item.key}-pagination`}
              type="button"
              aria-label={item.ariaLabel}
              className={cn(
                "h-1.5 rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                index === activeImageIndex
                  ? "w-3 bg-brand"
                  : "w-1.5 bg-foreground/40",
              )}
              onClick={(event) => {
                event.stopPropagation()
                setIsPreviewing(index > 0)
                setActiveImageIndex(index)
              }}
            />
          ))}
        </div>
      </div>

      <div className="flex min-h-[148px] flex-col gap-4 p-4">
        <div className="flex flex-col gap-2">
          <div className="font-mono text-xs text-foreground/50">
            {series.eyebrow}
          </div>
          <h2 className="font-headline text-xl leading-tight text-foreground">
            {series.name}
          </h2>
          {lowestPrice ? (
            <div className="font-mono text-xs text-foreground/60">
              From <span className="text-foreground">{lowestPrice}</span>
            </div>
          ) : null}
        </div>

        <div className="mt-auto flex flex-col gap-2">
          <Button
            type="button"
            variant="default"
            className="h-9 min-h-9 w-full"
            onClick={(event) => {
              event.stopPropagation()
              onOpenPack()
            }}
          >
            Crack a Pack
          </Button>
        </div>
      </div>
    </Card>
  )
}

export function shouldUsePackShowcase({
  index,
  source,
}: {
  index: number
  source?: string
}) {
  return index > 0 && source === "config"
}

function PackShowcase({
  image,
  videoSrc,
}: {
  image: string
  videoSrc?: string
}) {
  const blurredMediaClassName =
    "absolute h-[82%] w-[50%] object-cover object-center opacity-35 blur-[6px] brightness-75 transition-opacity duration-500"
  const foregroundMediaClassName =
    "relative z-10 h-[73%] w-[73%] object-contain drop-shadow-[0_20px_32px_rgba(0,0,0,0.48)] transition-opacity duration-500"

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-[#101010]">
      <PackShowcaseMedia
        image={image}
        videoSrc={videoSrc}
        className={`${blurredMediaClassName} left-[-18%]`}
      />
      <PackShowcaseMedia
        image={image}
        videoSrc={videoSrc}
        className={`${blurredMediaClassName} right-[-18%]`}
      />
      <div className="absolute inset-0 bg-black/20" />
      <PackShowcaseMedia
        image={image}
        videoSrc={videoSrc}
        className={foregroundMediaClassName}
      />
    </div>
  )
}

function PackShowcaseMedia({
  image,
  videoSrc,
  className,
}: {
  image: string
  videoSrc?: string
  className: string
}) {
  if (videoSrc) {
    return (
      <video
        aria-hidden="true"
        autoPlay
        className={className}
        loop
        muted
        playsInline
        preload="auto"
        src={videoSrc}
        tabIndex={-1}
      />
    )
  }

  return <img src={image} alt="" className={className} />
}
